import { useState, useMemo, useCallback, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { type MockNCR, type MockCAPA } from '@/mocks/qms';
import { qcService } from '@/services/qcService';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import NCRDetailSlideOver from './components/NCRDetailSlideOver';
import RaiseNCRSlideOver from './components/RaiseNCRSlideOver';
import CAPAFormSlideOver from './components/CAPAFormSlideOver';
import CAPAVerifyModal from './components/CAPAVerifyModal';
import NCRReportsTab from './components/NCRReportsTab';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

const severityBadge = (severity: MockNCR['severity']) => {
  const map: Record<string, string> = {
    MINOR: 'bg-blue-100 text-blue-700',
    MAJOR: 'bg-amber-100 text-amber-700',
    CRITICAL: 'bg-red-100 text-red-700',
  };
  return map[severity] || 'bg-gray-100 text-gray-700';
};

const statusBadge = (status: MockNCR['status']) => {
  const map: Record<string, string> = {
    OPEN: 'bg-red-100 text-red-700',
    UNDER_REVIEW: 'bg-amber-100 text-amber-700',
    CAPA_RAISED: 'bg-violet-100 text-violet-700',
    CAPA_VERIFIED: 'bg-blue-100 text-blue-700',
    CLOSED: 'bg-emerald-100 text-emerald-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const dispositionBadge = (action: MockNCR['dispositionAction']) => {
  const map: Record<string, string> = {
    REJECT: 'bg-red-100 text-red-700',
    REWORK: 'bg-amber-100 text-amber-700',
    USE_AS_IS: 'bg-emerald-100 text-emerald-700',
    RETURN_TO_SUPPLIER: 'bg-orange-100 text-orange-700',
    PENDING: 'bg-gray-100 text-gray-700',
  };
  return map[action] || 'bg-gray-100 text-gray-700';
};

const capaTypeBadge = (type: MockCAPA['type']) => {
  const map: Record<string, string> = {
    CORRECTIVE: 'bg-blue-100 text-blue-700',
    PREVENTIVE: 'bg-emerald-100 text-emerald-700',
  };
  return map[type] || 'bg-gray-100 text-gray-700';
};

const capaStatusBadge = (status: MockCAPA['status']) => {
  const map: Record<string, string> = {
    OPEN: 'bg-gray-100 text-gray-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-emerald-100 text-emerald-700',
    VERIFIED: 'bg-violet-100 text-violet-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const rootCauseBadge = (cat: MockCAPA['rootCauseCategory']) => {
  const map: Record<string, string> = {
    MATERIAL: 'bg-orange-100 text-orange-700',
    MACHINE: 'bg-gray-100 text-gray-700',
    METHOD: 'bg-blue-100 text-blue-700',
    MANPOWER: 'bg-amber-100 text-amber-700',
    MEASUREMENT: 'bg-violet-100 text-violet-700',
    ENVIRONMENT: 'bg-emerald-100 text-emerald-700',
  };
  return map[cat] || 'bg-gray-100 text-gray-700';
};

export default function NCRPage() {
  const toast = useToast();
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);
  const [ncrs, setNcrs] = useState<MockNCR[]>([]);
  const [capas, setCapas] = useState<MockCAPA[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState<'ncr' | 'capa' | 'reports'>('ncr');

  const [detailNCR, setDetailNCR] = useState<MockNCR | null>(null);
  const [raiseNCROpen, setRaiseNCROpen] = useState(false);
  const [prefillInspectionId, setPrefillInspectionId] = useState<string | null>(null);
  const [capaFormNCR, setCapaFormNCR] = useState<MockNCR | null>(null);
  const [verifyCAPA, setVerifyCAPA] = useState<MockCAPA | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ncrData, capaData] = await Promise.all([
        qcService.getNCRs({ warehouseId: selectedWarehouseId || undefined }),
        qcService.getCAPAs()
      ]);
      setNcrs(ncrData || []);
      setCapas(capaData || []);
    } catch (err) {
      console.error('Error loading NCR/CAPA data:', err);
      setNcrs([]);
      setCapas([]);
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredNCRs = useMemo(() => {
    return ncrs.filter((ncr) => {
      if (typeFilter && ncr.type !== typeFilter) return false;
      if (severityFilter && ncr.severity !== severityFilter) return false;
      if (statusFilter && ncr.status !== statusFilter) return false;
      if (dateFrom && ncr.createdAt < dateFrom) return false;
      if (dateTo && ncr.createdAt > `${dateTo}T23:59:59`) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          ncr.ncrNumber.toLowerCase().includes(q) ||
          ncr.itemName.toLowerCase().includes(q) ||
          ncr.sourceNumber.toLowerCase().includes(q) ||
          ncr.defectDescription.toLowerCase().includes(q) ||
          ncr.inspectionNumber.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [ncrs, typeFilter, severityFilter, statusFilter, search, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const total = ncrs.length;
    const open = ncrs.filter((n) => n.status === 'OPEN').length;
    const underReview = ncrs.filter((n) => n.status === 'UNDER_REVIEW').length;
    const capaRaised = ncrs.filter((n) => n.status === 'CAPA_RAISED').length;
    const closed = ncrs.filter((n) => n.status === 'CLOSED').length;
    return { total, open, underReview, capaRaised, closed };
  }, [ncrs]);

  const handleStatusChange = useCallback(
    async (ncrId: string, newStatus: MockNCR['status']) => {
      try {
        await qcService.updateNCRStatus(ncrId, { status: newStatus });
        toast.success(`NCR status updated to ${newStatus.replace(/_/g, ' ')}`);
        await loadData();
      } catch (err) {
        console.error('Error updating NCR status:', err);
      }
    },
    [loadData, toast]
  );

  const handleStartReview = useCallback(
    async (ncrId: string) => {
      try {
        await qcService.updateNCRStatus(ncrId, {
          status: 'UNDER_REVIEW',
          reviewedBy: 'Admin User'
        });
        toast.success('NCR moved to Under Review');
        await loadData();
      } catch (err) {
        console.error('Error reviewing NCR:', err);
      }
    },
    [loadData, toast]
  );

  const handleUpdateNCR = useCallback(
    async (updated: MockNCR) => {
      try {
        await qcService.updateNCRStatus(updated.id, {
          status: updated.status,
          dispositionAction: updated.dispositionAction,
          reviewedBy: updated.reviewedBy || undefined,
          closedDate: updated.closedDate || undefined
        });
        await loadData();
        setDetailNCR(updated);
      } catch (err) {
        console.error('Error updating NCR details:', err);
      }
    },
    [loadData]
  );

  const handleRaiseCAPA = useCallback(
    (ncr: MockNCR) => {
      setDetailNCR(null);
      setCapaFormNCR(ncr);
    },
    []
  );

  const handleVerifyCAPA = useCallback(
    (ncr: MockNCR) => {
      const capa = capas.find((c) => c.ncrId === ncr.id);
      if (!capa) {
        toast.error('No CAPA found for this NCR');
        return;
      }
      if (capa.status !== 'COMPLETED') {
        toast.warning('CAPA must be completed before verification');
        return;
      }
      setDetailNCR(null);
      setVerifyCAPA(capa);
    },
    [capas, toast]
  );

  const handleCAPACreated = useCallback(
    async (capaPayload: Partial<MockCAPA>) => {
      try {
        await qcService.createCAPA(capaPayload);
        toast.success('CAPA successfully raised');
        await loadData();
      } catch (err) {
        console.error('Error raising CAPA:', err);
      } finally {
        setCapaFormNCR(null);
      }
    },
    [loadData, toast]
  );

  const handleCAPAVerified = useCallback(
    async () => {
      await loadData();
      setVerifyCAPA(null);
    },
    [loadData]
  );

  const handleNCRCreated = useCallback(
    async (ncrPayload: Partial<MockNCR>) => {
      try {
        await qcService.createNCR(ncrPayload);
        toast.success('NCR raised successfully');
        await loadData();
      } catch (err) {
        console.error('Error creating NCR:', err);
      } finally {
        setRaiseNCROpen(false);
        setPrefillInspectionId(null);
      }
    },
    [loadData, toast]
  );

  const openRaiseNCR = useCallback(() => {
    setPrefillInspectionId(null);
    setRaiseNCROpen(true);
  }, []);

  const handleCAPAStart = useCallback(
    async (capaId: string) => {
      try {
        await qcService.updateCAPAStatus(capaId, 'IN_PROGRESS');
        toast.success('CAPA marked In Progress');
        await loadData();
      } catch (err) {
        console.error('Error starting CAPA:', err);
      }
    },
    [loadData, toast]
  );

  const handleCAPAComplete = useCallback(
    async (capaId: string) => {
      try {
        await qcService.updateCAPAStatus(capaId, 'COMPLETED');
        toast.success('CAPA marked Complete');
        await loadData();
      } catch (err) {
        console.error('Error completing CAPA:', err);
      }
    },
    [loadData, toast]
  );

  const handleCAPAVerifyClick = useCallback(
    (capa: MockCAPA) => {
      if (capa.status !== 'COMPLETED') {
        toast.warning('CAPA must be completed before verification');
        return;
      }
      setVerifyCAPA(capa);
    },
    [toast]
  );

  const handleCloseNCR = useCallback(
    async (ncr: MockNCR) => {
      if (ncr.status !== 'CAPA_VERIFIED') {
        toast.error('NCR must be CAPA Verified before closing');
        return;
      }
      try {
        await qcService.updateNCRStatus(ncr.id, {
          status: 'CLOSED',
          closedDate: new Date().toISOString().split('T')[0]
        });
        toast.success('NCR closed');
        await loadData();
      } catch (err) {
        console.error('Error closing NCR:', err);
      }
    },
    [loadData, toast]
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Non-Conformance Reports</h1>
            <p className="text-sm text-gray-500 mt-0.5">Track and manage quality non-conformances and CAPA</p>
          </div>
          <button
            onClick={openRaiseNCR}
            className="px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-1" />
            Raise NCR
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Total NCRs', value: stats.total, icon: 'ri-file-warning-line', color: 'gray' },
            { label: 'Open', value: stats.open, icon: 'ri-error-warning-line', color: 'red' },
            { label: 'Under Review', value: stats.underReview, icon: 'ri-search-line', color: 'amber' },
            { label: 'CAPA Raised', value: stats.capaRaised, icon: 'ri-shield-check-line', color: 'violet' },
            { label: 'Closed', value: stats.closed, icon: 'ri-check-double-line', color: 'emerald' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-${s.color}-50 flex items-center justify-center`}>
                  <i className={`${s.icon} text-${s.color}-600 text-lg`} />
                </div>
                <div>
                  <p className={`text-2xl font-semibold text-${s.color}-700`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab('ncr')}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer ${
              activeTab === 'ncr'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            NCR List ({filteredNCRs.length})
          </button>
          <button
            onClick={() => setActiveTab('capa')}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer ${
              activeTab === 'capa'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            CAPA List ({capas.length})
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer ${
              activeTab === 'reports'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Reports
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row gap-3 items-end">
            <div className="flex-1 w-full lg:w-auto">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Search</label>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search NCR, item, defect, source..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="w-full lg:w-40">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
              <SearchableSelect
                value={typeFilter}
                onChange={(val) => setTypeFilter(val)}
                options={[
                  { value: '', label: 'All Types' },
                  { value: 'INCOMING', label: 'Incoming' },
                  { value: 'IN_PROCESS', label: 'In-Process' },
                  { value: 'FINAL', label: 'Final' }
                ]}
                placeholder="All Types"
              />
            </div>
            <div className="w-full lg:w-40">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Severity</label>
              <SearchableSelect
                value={severityFilter}
                onChange={(val) => setSeverityFilter(val)}
                options={[
                  { value: '', label: 'All Severity' },
                  { value: 'MINOR', label: 'Minor' },
                  { value: 'MAJOR', label: 'Major' },
                  { value: 'CRITICAL', label: 'Critical' }
                ]}
                placeholder="All Severity"
              />
            </div>
            <div className="w-full lg:w-44">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
              <SearchableSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'OPEN', label: 'Open' },
                  { value: 'UNDER_REVIEW', label: 'Under Review' },
                  { value: 'CAPA_RAISED', label: 'CAPA Raised' },
                  { value: 'CAPA_VERIFIED', label: 'CAPA Verified' },
                  { value: 'CLOSED', label: 'Closed' }
                ]}
                placeholder="All Status"
              />
            </div>
            <div className="w-full lg:w-36">
              <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="w-full lg:w-36">
              <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <button
              onClick={() => {
                setSearch('');
                setTypeFilter('');
                setSeverityFilter('');
                setStatusFilter('');
                setDateFrom('');
                setDateTo('');
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-filter-off-line mr-1" />
              Clear
            </button>
          </div>
        </div>

        {/* NCR Table */}
        {activeTab === 'ncr' && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">NCR No</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Type</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Severity</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Item</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Source</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Defect</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Disposition</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Status</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredNCRs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <i className="ri-inbox-line text-3xl" />
                          <p>No non-conformance reports found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredNCRs.map((ncr) => (
                      <tr key={ncr.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{ncr.ncrNumber}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ncr.type.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityBadge(ncr.severity)}`}>
                            {ncr.severity}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 max-w-[160px]">
                          <p className="truncate" title={ncr.itemName}>{ncr.itemName}</p>
                          {ncr.variantName && <p className="text-xs text-gray-400 truncate">{ncr.variantName}</p>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ncr.sourceNumber}</td>
                        <td className="px-4 py-3 text-gray-700 max-w-[200px]">
                          <p className="truncate" title={ncr.defectDescription}>{ncr.defectDescription}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${dispositionBadge(ncr.dispositionAction)}`}>
                            {ncr.dispositionAction.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(ncr.status)}`}>
                            {ncr.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setDetailNCR(ncr)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded cursor-pointer"
                              title="View Details"
                            >
                              <i className="ri-eye-line" />
                            </button>
                            {!capas.some((c) => c.ncrId === ncr.id) && ncr.status !== 'CLOSED' && (
                              <button
                                onClick={() => handleRaiseCAPA(ncr)}
                                className="p-1.5 text-gray-500 hover:text-violet-600 hover:bg-violet-50 rounded cursor-pointer"
                                title="Raise CAPA"
                              >
                                <i className="ri-shield-check-line" />
                              </button>
                            )}
                            {ncr.status === 'OPEN' && (
                              <button
                                onClick={() => handleStartReview(ncr.id)}
                                className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded cursor-pointer"
                                title="Start Review"
                              >
                                <i className="ri-search-line" />
                              </button>
                            )}
                            {ncr.status === 'CAPA_VERIFIED' && (
                              <button
                                onClick={() => handleCloseNCR(ncr)}
                                className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"
                                title="Close NCR"
                              >
                                <i className="ri-check-double-line" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CAPA Table */}
        {activeTab === 'reports' && <NCRReportsTab ncrs={ncrs} />}

        {activeTab === 'capa' && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">CAPA No</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">NCR</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Type</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Root Cause</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Assigned</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Target Date</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Status</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {capas.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <i className="ri-inbox-line text-3xl" />
                          <p>No CAPAs found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    capas.map((capa) => (
                      <tr key={capa.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{capa.capaNumber}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{capa.ncrNumber}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${capaTypeBadge(capa.type)}`}>
                            {capa.type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${rootCauseBadge(capa.rootCauseCategory)}`}>
                            {capa.rootCauseCategory.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{capa.assignedToName}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{capa.targetDate}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${capaStatusBadge(capa.status)}`}>
                            {capa.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {capa.status === 'OPEN' && (
                              <button
                                onClick={() => handleCAPAStart(capa.id)}
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded cursor-pointer"
                                title="Start CAPA"
                              >
                                <i className="ri-play-circle-line" />
                              </button>
                            )}
                            {capa.status === 'IN_PROGRESS' && (
                              <button
                                onClick={() => handleCAPAComplete(capa.id)}
                                className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded cursor-pointer"
                                title="Mark Complete"
                              >
                                <i className="ri-check-line" />
                              </button>
                            )}
                            {capa.status === 'COMPLETED' && (
                              <button
                                onClick={() => handleCAPAVerifyClick(capa)}
                                className="p-1.5 text-gray-500 hover:text-violet-600 hover:bg-violet-50 rounded cursor-pointer"
                                title="Verify CAPA"
                              >
                                <i className="ri-shield-check-line" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals / Slide-overs */}
      {detailNCR && (
        <NCRDetailSlideOver
          ncr={detailNCR}
          onClose={() => setDetailNCR(null)}
          onStatusChange={handleStatusChange}
          onUpdateNCR={handleUpdateNCR}
          onRaiseCAPA={handleRaiseCAPA}
          onVerifyCAPA={handleVerifyCAPA}
        />
      )}

      {raiseNCROpen && (
        <RaiseNCRSlideOver
          prefillInspection={null}
          onClose={() => {
            setRaiseNCROpen(false);
            setPrefillInspectionId(null);
          }}
          onCreated={handleNCRCreated}
        />
      )}

      {capaFormNCR && (
        <CAPAFormSlideOver
          ncr={capaFormNCR}
          onClose={() => setCapaFormNCR(null)}
          onCreated={handleCAPACreated}
        />
      )}

      {verifyCAPA && (
        <CAPAVerifyModal
          capa={verifyCAPA}
          onClose={() => setVerifyCAPA(null)}
          onVerified={handleCAPAVerified}
        />
      )}
    </AppLayout>
  );
}